import requestConfig from '../../shared/config/request';
import APIError, { APIErrorData } from '../../shared/errors/api-error';
import NetworkError from '../../shared/errors/network-error';
import reportToSentry from '../../shared/utils/report-to-sentry';
import { getFirebaseToken, getFirebaseTokenFromPlatform } from '../auth';

enum Methods {
  POST = 'POST',
  PUT = 'PUT',
  GET = 'GET',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
}

const defaultHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

async function parseResponseToJson<T>(response: Response) {
  try {
    const result = (await response.json()) as T;
    return result;
  } catch {
    throw new Error('Failed to parse json response');
  }
}

export async function makeRequest<T>(
  url: string,
  options?: RequestInit & { useDefaultHeaders?: boolean },
  isAuthRequired: boolean = false,
  retry: boolean = true
): Promise<T> {
  // init the token as a string or undefined
  let token: string | undefined;
  const parentOrigin = window.localStorage.getItem('parent-origin');
  const backupParentOrigin = window.location?.ancestorOrigins?.[0] || '';
  // if the flag isAuthRequired is true
  if (isAuthRequired) {
    // get the auth token from firebase and assign the same to token
    if (!retry) {
      await getFirebaseTokenFromPlatform();
    }
    //TODO: getFirebaseToken is a function that returns a promise
    if (!retry) {
      await getFirebaseTokenFromPlatform();
    }
    token = await getFirebaseToken();
    if (!token) {
      const errorResult: APIErrorData = {
        error: 'Unauthorized',
        message: 'Unauthorized Request',
        statusCode: 401,
      };
      throw new APIError(errorResult);
    }
  }
  const {
    method = Methods.GET,
    body,
    headers,
    useDefaultHeaders = true,
  } = options ?? {};

  let response: Response;
  try {
    response = await fetch(
      url.startsWith('http') ? url : `${requestConfig.baseUrl}${url}`,
      {
        method,
        body,
        headers: {
          ...(useDefaultHeaders ? defaultHeaders : {}),
          ...headers,
          ...(token && { Authorization: token }),
        'custom-origin': decodeURIComponent(parentOrigin || backupParentOrigin  || ''),
        },
      }
    );
  } catch {
    throw new NetworkError();
  }

  if (response.status === 401 && retry) {
    //TODO refresh token
    return makeRequest(url, options, isAuthRequired, false);
  }

  if (!response.ok) {
    const errorResult = await parseResponseToJson<APIErrorData>(response);
    const error = new APIError(errorResult);
    reportToSentry(error);
    throw new APIError(errorResult);
  }

  return parseResponseToJson<T>(response);
}

export async function get<T>(
  url: string,
  options?: RequestInit & { useDefaultHeaders?: boolean },
  isAuthRequired?: boolean
) {
  return makeRequest<T>(
    url,
    {
      ...options,
      method: Methods.GET,
    },
    isAuthRequired
  );
}

export async function post<T>(
  url: string,
  options?: RequestInit & { useDefaultHeaders?: boolean },
  isAuthRequired?: boolean
) {
  return makeRequest<T>(
    url,
    {
      ...options,
      method: Methods.POST,
    },
    isAuthRequired
  );
}

export async function put<T>(
  url: string,
  options?: RequestInit & { useDefaultHeaders?: boolean },
  isAuthRequired?: boolean
) {
  return makeRequest<T>(
    url,
    {
      ...options,
      method: Methods.PUT,
    },
    isAuthRequired
  );
}

export async function patch<T>(
  url: string,
  options?: RequestInit & { useDefaultHeaders?: boolean },
  isAuthRequired?: boolean
) {
  return makeRequest<T>(
    url,
    {
      ...options,
      method: Methods.PATCH,
    },
    isAuthRequired
  );
}

export async function deleteReq<T>(
  url: string,
  options?: RequestInit & { useDefaultHeaders?: boolean },
  isAuthRequired?: boolean
) {
  return makeRequest<T>(
    url,
    {
      ...options,
      method: Methods.DELETE,
    },
    isAuthRequired
  );
}
